import { ITeam } from '@livepolls/ui-components/src/interfaces/team.interface';
import { useEffect } from 'react';
import { DEFAULT_TEAM_NAMES } from 'src/constants/default-team-names.constants';
import { TextInput } from 'src/wick-ui/input/TextInput';
import { v4 as uuid } from 'uuid';
import { ColorPicker } from '../color-picker/ColorPicker';
import styles from './team-setting.module.css';

interface Props {
  teamSize: number;
  teams: ITeam[];
  anonymousResponses: boolean;
  manualAssignTeams: boolean;
  onChangeTeamSize: (value: number) => void;
  onChangeManualAssign: (value: boolean) => void;
  onChangeTeams: (cb: (v: ITeam[]) => ITeam[]) => void;
}

const DEFAULT_TEAM_SIZE = 2;
const DEFAULT_TEAM_COLORS = ['#E53251', '#00E678', '#F5A300', '#00B4FF'];

const createTeam = (teamNum: number): ITeam => {
  return {
    id: uuid(),
    color: DEFAULT_TEAM_COLORS[teamNum - 1],
    name: '',
  };
};

export const TeamSetting = ({
  teamSize,
  teams,
  anonymousResponses,
  onChangeTeamSize,
  onChangeManualAssign,
  onChangeTeams,
}: Props) => {
  useEffect(() => {
    onChangeTeamSize(DEFAULT_TEAM_SIZE);
    onChangeTeams(_ => [createTeam(1), createTeam(2)]);
  }, [onChangeTeamSize, onChangeTeams]);

  useEffect(() => {
    if (anonymousResponses) {
      onChangeManualAssign(false);
    }
  }, [anonymousResponses, onChangeManualAssign]);

  const updateTeam = (updatedTeam: ITeam, index: number) => {
    onChangeTeams(oldTeams => {
      return [
        ...oldTeams.slice(0, index),
        updatedTeam,
        ...oldTeams.slice(index + 1),
      ];
    });
  };

  const handleChangeTeamName = (newName: string, index: number) => {
    const teamToUpdate = teams[index];
    const updatedTeam = { ...teamToUpdate, name: newName };
    updateTeam(updatedTeam, index);
  };

  const handleChangeTeamColor = (newColor: string, index: number) => {
    const teamToUpdate = teams[index];
    const updatedTeam = { ...teamToUpdate, color: newColor };
    updateTeam(updatedTeam, index);
  };

  const handleChangeTeamSize = (newTeamSize: number) => {
    onChangeTeamSize(newTeamSize);
    const previousTeamSize = teams.length;
    if (previousTeamSize < newTeamSize) {
      const newTeams = [] as ITeam[];
      for (let i = previousTeamSize + 1; i <= newTeamSize; i++) {
        newTeams.push(createTeam(i));
      }
      onChangeTeams(oldTeams => [...oldTeams, ...newTeams]);
    } else if (previousTeamSize > newTeamSize) {
      onChangeTeams(oldTeams => [...oldTeams.slice(0, newTeamSize)]);
    }
  };

  const getTeamDetails = (teamIndex: number) => {
    return (
      <div className={styles.teamDetails}>
        <div className={styles.teamIndex}>Team {teamIndex + 1}</div>
        <div className={styles.teamNameContainer}>
          <TextInput
            placeholder={DEFAULT_TEAM_NAMES[teamIndex]}
            onChange={newName => handleChangeTeamName(newName, teamIndex)}
            maxLength={20}
          />
          <ColorPicker
            colorInHex={teams[teamIndex]?.color}
            onChange={newColor => handleChangeTeamColor(newColor, teamIndex)}
            customClass={styles.customColorPicker}
          />
        </div>

        <div className={styles.teamNameLength} data-testid="teamNameLength">
          {teams[teamIndex]?.name.length}
          <span>/20</span>
        </div>
      </div>
    );
  };

  const selectTeamSizeDiv = (selectedTeamSize: number) => {
    return (
      <div
        className={`${styles.teamNumber || ''} ${
          teamSize === selectedTeamSize ? styles.activeTeamSize : ''
        }`}
        onClick={() => handleChangeTeamSize(selectedTeamSize)}
      >
        {selectedTeamSize}
      </div>
    );
  };

  return (
    <>
      <div className={styles.teamSettingsContainer}>
        <div className={styles.numberOfTeams}>
          {selectTeamSizeDiv(2)}
          {addDivider(1, teamSize)}
          {selectTeamSizeDiv(3)}
          {addDivider(2, teamSize)}
          {selectTeamSizeDiv(4)}
        </div>

        <div className={styles.teamsRow1}>
          <div className={styles.team}>{getTeamDetails(0)}</div>
          <div className={styles.team}>{getTeamDetails(1)}</div>
        </div>
        {teamSize > 2 && (
          <div className={styles.teamsRow2}>
            <div className={styles.team}>{getTeamDetails(2)}</div>
            {teamSize === 4 && (
              <div className={styles.team}>{getTeamDetails(3)}</div>
            )}

            {teamSize === 3 && <div className={styles.team}></div>}
          </div>
        )}
      </div>
    </>
  );
};

const addDivider = (dividerNumber: number, teamSize: number) => {
  if (dividerNumber === 1 && (teamSize === 2 || teamSize === 3)) {
    return null;
  }
  if (dividerNumber === 2 && (teamSize === 3 || teamSize === 4)) {
    return null;
  }
  return <div className={styles.divider}></div>;
};
